import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { expandSidebarFavoritesSection } from "@/hooks/useLocalStorageState";
import { EntityType } from "@/queries/folder-entities/types";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export interface FavoriteStarUser {
  id: number;
  email: string;
  name: string;
}

export interface GetFavoriteStarsResponse {
  entity_type: string;
  entity_id: string;
  users: FavoriteStarUser[];
  has_prev: boolean;
  has_next: boolean;
  prev_num: number | null;
  next_num: number | null;
  page: number;
  pages: number;
  per_page: number;
  total: number;
}

export interface FavoriteItem {
  entity_type: EntityType;
  entity_id: string;
  name: string;
}

export interface ListFavoritesResponse {
  success: boolean;
  favorites: FavoriteItem[];
}

export const favoritesListQueryKey = (workspaceId: number) => ["favorites", workspaceId];

export const favoriteStarsQueryKey = (
  workspaceId: number,
  entityType: EntityType,
  entityId: string,
) => ["favorite-stars", workspaceId, entityType, entityId];

export const invalidateFavoritesList = (
  queryClient: QueryClient,
  workspaceId?: number | string | null,
) => {
  if (workspaceId !== undefined && workspaceId !== null && workspaceId !== "") {
    queryClient.invalidateQueries({
      queryKey: favoritesListQueryKey(Number(workspaceId)),
    });
    return;
  }

  queryClient.invalidateQueries({ queryKey: ["favorites"] });
};

export const patchFavoriteDisplayName = (
  queryClient: QueryClient,
  {
    workspaceId,
    entityType,
    entityId,
    name,
  }: {
    workspaceId: number;
    entityType: EntityType;
    entityId: string | number;
    name: string;
  },
) => {
  queryClient.setQueryData<ListFavoritesResponse>(favoritesListQueryKey(workspaceId), old => {
    if (!old) {
      return old;
    }

    const normalizedId = String(entityId);
    return {
      ...old,
      favorites: old.favorites.map(favorite =>
        favorite.entity_type === entityType && favorite.entity_id === normalizedId
          ? { ...favorite, name }
          : favorite,
      ),
    };
  });
};
export const useGetFavoriteStars = ({
  workspaceId,
  entityType,
  entityId,
  enabled = true,
}: {
  workspaceId: number;
  entityType: EntityType;
  entityId: string;
  enabled?: boolean;
}) => {
  const authContext = useAuth();

  return useQuery<GetFavoriteStarsResponse>({
    queryKey: favoriteStarsQueryKey(workspaceId, entityType, entityId),
    queryFn: async () => {
      const url = new URL(ENDPOINTS.favorite_stars(workspaceId, entityType, entityId));
      url.searchParams.set("page", "1");
      url.searchParams.set("per_page", "5");

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
      });

      return handleResponse(response);
    },
    enabled:
      enabled &&
      !!authContext?.userToken &&
      !!workspaceId &&
      !!entityType &&
      !!entityId &&
      entityId !== "-1",
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });
};

export const useListFavorites = ({
  workspaceId,
  enabled = true,
}: {
  workspaceId: number;
  enabled?: boolean;
}) => {
  const authContext = useAuth();

  return useQuery<ListFavoritesResponse>({
    queryKey: favoritesListQueryKey(workspaceId),
    queryFn: async () => {
      const response = await fetch(ENDPOINTS.favorites(workspaceId), {
        method: "GET",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
      });

      return handleResponse(response);
    },
    enabled: enabled && !!authContext?.userToken && !!workspaceId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });
};

interface FavoriteMutationRequest {
  workspaceId: number;
  entityType: EntityType;
  entityId: string;
}

interface FavoriteMutationResponse {
  starred: boolean;
  entity_type: string;
  entity_id: string;
}

const invalidateFavoriteQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: number,
  entityType: EntityType,
  entityId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: favoriteStarsQueryKey(workspaceId, entityType, entityId),
  });
  queryClient.invalidateQueries({
    queryKey: favoritesListQueryKey(workspaceId),
  });
};

export const useStarFavorite = () => {
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation<FavoriteMutationResponse, Error, FavoriteMutationRequest>({
    mutationFn: async ({ workspaceId, entityType, entityId }) => {
      const response = await fetch(ENDPOINTS.favorites(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
      });

      return handleResponse(response);
    },
    onSuccess: (_data, { workspaceId, entityType, entityId }) => {
      invalidateFavoriteQueries(queryClient, workspaceId, entityType, entityId);
      expandSidebarFavoritesSection();
    },
    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to star");
    },
  });
};

export const useUnstarFavorite = () => {
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation<FavoriteMutationResponse, Error, FavoriteMutationRequest>({
    mutationFn: async ({ workspaceId, entityType, entityId }) => {
      const response = await fetch(ENDPOINTS.favorites(workspaceId), {
        method: "DELETE",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
      });

      return handleResponse(response);
    },
    onSuccess: (_data, { workspaceId, entityType, entityId }) => {
      invalidateFavoriteQueries(queryClient, workspaceId, entityType, entityId);
      expandSidebarFavoritesSection();
    },
    onError: (error: Error) => {
      displayErrorToast(error.message || "Failed to unstar");
    },
  });
};
